<template>
    <grid
        ref="grid"
        :columnDefs="columnDefs"
        :fetchCallback="fetchCallback"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>
        <b-button-group aria-label="Export table data">
            <responsive-button
                :icon="['fas', 'file-excel']"
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            >
            </responsive-button>
        </b-button-group>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
        >
            Refresh
        </responsive-button>
    </grid>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../../api';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../../generic/grid/grid.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';

import {
    SUBSCRIBER_WORDPRESS_INSTALLATIONS_GRID_ENDPOINT,
    SUBSCRIBER_WORDPRESS_INSTALLATIONS_EXCEL_ENDPOINT,
} from '../../../../endpoints';

export default {
    name: 'SubscriberWordpressList',
    components: {
        Grid,
        ResponsiveButton,
        DateRangePicker,
        actionsCellRenderer: ActionsCellRenderer,
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(
                    SUBSCRIBER_WORDPRESS_INSTALLATIONS_GRID_ENDPOINT(),
                    {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            from_date: this.fromDateRequest,
                            to_date: this.toDateRequest,
                        },
                    }
                );
            };
        },
        fromDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        toDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportExcelHref() {
            return (
                `${SUBSCRIBER_WORDPRESS_INSTALLATIONS_EXCEL_ENDPOINT()}` +
                `?offset=${this.offset}` +
                `&limit=${this.limit}` +
                `&sort=${this.sort}` +
                `&desc=${this.desc}` +
                `&search=${this.search}` +
                `&from_date=${this.fromDateRequest}` +
                `&to_date=${this.toDateRequest}`
            );
        },
    },
    data() {
        return {
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
            columnDefs: [
                {
                    field: 'id',
                    headerName: 'ID',
                    minWidth: 100,
                    maxWidth: 100,
                    sort: 'asc',
                },
                {
                    field: 'date_joined',
                    headerName: 'Date Joined',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'first_name',
                    headerName: 'First Name',
                },
                {
                    field: 'last_name',
                    headerName: 'Last Name',
                },
                {
                    field: 'email',
                    headerName: 'Email',
                    minWidth: 300,
                },
                {
                    field: 'is_active',
                    headerName: 'Active',
                    type: 'yesNoColumn',
                    minWidth: 100,
                    maxWidth: 100,
                },
                {
                    field: 'is_validated',
                    headerName: 'Validated',
                    type: 'yesNoColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'active_subscription',
                    headerName: 'Subscription',
                    type: 'yesNoColumn',
                    sortable: false,
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'url',
                    headerName: 'Url',
                    type: 'linkColumn',
                    sortable: false,
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                },
            ],
            dateRange: nameToRange(ALL_DATES),
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
        };
    },
    methods: {
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
};
</script>